/**
 * Document Type Mapper — Frontend (DEV-6164 Phase 6).
 *
 * The bilingual labels that used to live here have moved to
 * {@code src/locales/{fr,en}.json} under {@code documentTypes.<KEY>.label}
 * / {@code .shortLabel}, plus {@code documentStatuses.*} and
 * {@code documentComments.*}. This module is now a thin key resolver —
 * the actual i18n lookup happens at render time in the component via
 * {@code useTranslation().t(key)}, per the project rule "no t() in utils".
 *
 * The accepted input dialects on every helper:
 * - Portal UPPER_SNAKE  ("PAYMENT_PROOF", "MEDICAL_REPORT", …) — canonical
 * - Docmana camelCase   ("isPaymentProof", "isMedicalReport", …) — normalised
 *
 * Unknown inputs fall back to the raw value as the i18n key (which will
 * resolve to the raw value via i18next's default key-as-fallback behaviour).
 */

/**
 * Normalise a Docmana camelCase key to the canonical Portal UPPER_SNAKE
 * identifier used in the locale catalog. Pass-through for inputs that
 * already look like Portal keys.
 */
const DOCMANA_TO_PORTAL: Readonly<Record<string, string>> = Object.freeze({
  // Legacy Docmana camelCase
  isPaymentProof: 'PAYMENT_PROOF',
  isMedicalReport: 'MEDICAL_REPORT',
  isPrescription: 'PRESCRIPTION',
  isMedicalBill: 'MEDICAL_BILL',
  isPrescriptionBill: 'PRESCRIPTION_BILL',
  isHcfa1500: 'HCFA_1500',
  isUb04: 'UB_04',
  isTransportationBill: 'TRANSPORTATION_BILL',
  isTravelProof: 'TRAVEL_PROOF',
  isACheque: 'VOID_CHECK',
  isBankStatement: 'BANK_STATEMENT',
  isMandateForm: 'MANDATE_FORM',
  isStudentProof: 'STUDENT_PROOF',

  // DEV-6164 subtype api-values (PascalCase) — DocumentTypeRef.subtype.
  // Subtypes that don't have a dedicated locale entry yet map to the closest
  // existing label key (e.g. NonMedicalBill → INVOICE display label).
  MedicalBill: 'MEDICAL_BILL',
  'HCFA-1500': 'HCFA_1500',
  'UB-04': 'UB_04',
  PrescriptionBill: 'PRESCRIPTION_BILL',
  TransportationBill: 'TRANSPORTATION_BILL',
  OtherTypeOfBill: 'OTHER_BILL',
  NonMedicalBill: 'INVOICE',
  TravelProof: 'TRAVEL_PROOF',
  PaymentProof: 'PAYMENT_PROOF',
  Prescription: 'PRESCRIPTION',
  MedicalReport: 'MEDICAL_REPORT',
  OtherTypeOfProof: 'OTHER_PROOF',
  TripCancellationProof: 'PROOF',
  BaggageProof: 'PROOF',
  CheckSample: 'VOID_CHECK',
});

export function toPortalType(documentType: string): string {
  if (!documentType) return documentType;
  return DOCMANA_TO_PORTAL[documentType] ?? documentType;
}

/**
 * i18n key for the full-form label of a document type. Pair with
 * {@code t(key, { defaultValue: type })} so unknown types fall back
 * to the raw input string.
 */
export function getDocumentTypeLabelKey(documentType: string): string {
  return `documentTypes.${toPortalType(documentType)}.label`;
}

/**
 * i18n key for the short / table-friendly label. The locale file
 * intentionally only provides {@code shortLabel} for types where a
 * meaningful abbreviation exists; the caller should use i18next's
 * key-resolution fallback (or {@link getDocumentTypeLabelKey}) when
 * the short label is absent.
 */
export function getShortDocumentTypeLabelKey(documentType: string): string {
  return `documentTypes.${toPortalType(documentType)}.shortLabel`;
}

/** i18n key for a backend document-analysis status string. */
export function getDocumentStatusLabelKey(status: string): string {
  return `documentStatuses.${status}`;
}

/** Label keys used as the fallback when the wire {@code documentType} doesn't match any known subtype. */
export const UNKNOWN_DOCUMENT_TYPE_SHORT_LABEL_KEY = 'documentTypes.UNKNOWN.shortLabel';
export const UNKNOWN_DOCUMENT_TYPE_LABEL_KEY = 'documentTypes.UNKNOWN.label';

/**
 * Outcome of resolving a wire {@code documentType} to its i18n keys.
 * Pair {@link isKnown} with a logger.warn to surface unmapped NOVA values for diagnostics
 * without leaking the raw PascalCase string into the UI.
 */
export interface ResolvedDocumentTypeLabel {
  /** i18n key for the short, row-friendly label (always populated after DEV-6974). */
  shortKey: string;
  /** i18n key for the long help-text label. */
  fullKey: string;
  /** False when {@code documentType} doesn't match any known Docmana legacy or DEV-6164 subtype. */
  isKnown: boolean;
}

export function resolveDocumentTypeLabel(documentType: string | null | undefined): ResolvedDocumentTypeLabel {
  if (!documentType || !(documentType in DOCMANA_TO_PORTAL)) {
    return {
      shortKey: UNKNOWN_DOCUMENT_TYPE_SHORT_LABEL_KEY,
      fullKey: UNKNOWN_DOCUMENT_TYPE_LABEL_KEY,
      isKnown: false,
    };
  }
  const portal = DOCMANA_TO_PORTAL[documentType];
  return {
    shortKey: `documentTypes.${portal}.shortLabel`,
    fullKey: `documentTypes.${portal}.label`,
    isKnown: true,
  };
}

/**
 * Translate a raw backend "comment" string (English phrases like
 * "Manual" or "Document is not a medical invoice") into the matching
 * locale-key path, or {@code null} when no translation exists.
 *
 * Callers should fall back to the raw comment when this returns null.
 */
export function getDocumentCommentKey(comment: string | null | undefined): string | null {
  if (!comment || comment.trim() === '') return null;
  return `documentComments.${comment}`;
}
